import React, { useCallback, useContext, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
  Grid,
  Avatar,
  Badge,
  Checkbox,
  Button,
} from "@material-ui/core";
import {
  Assignment as TaskIcon,
  Description as FormIcon,
  Timer as TimerIcon,
  CheckCircle as ConditionIcon,
  Build as SkillIcon,
  DateRange as DateIcon,
  Fingerprint as IdIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Print,
} from "@material-ui/icons";
import { useParams } from "react-router-dom";
import usePrintServiceType from "./actions/usePrintServiceType";
import { FilterOperator } from "shared/types";
import {
  TaskStep,
  FormStep,
  StepTypes,
  Condition,
} from "../../../../Maintenance/definitions/types";
import { useStyles } from "./styles";

export default function PrintServiceTypes() {
  const { data, isLoading } = usePrintServiceType();
  const classes = useStyles();

  // Helper function to format dates elegantly
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Helper function to render condition values elegantly
  const renderConditionValues = (condition: Condition) => {
    return (
      <Box style={{ marginTop: 16 }}>
        <Typography variant="subtitle2" gutterBottom>
          Condition Values ({condition.values.length}):
        </Typography>
        <Grid container spacing={1}>
          {condition.values.map((value, index) => (
            <Grid item key={index}>
              <Chip
                size="small"
                label={`${value.operator}: "${value.value}" → ${value.nextStep}`}
                variant="outlined"
                color="primary"
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // Helper function to render task step details
  const renderTaskStep = (step: TaskStep, index: number) => (
    <Card
      elevation={2}
      style={{
        marginBottom: 24,
        borderLeft: "4px solid #1976d2",
        backgroundColor: "#f8f9fa",
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <TaskIcon color="primary" style={{ marginRight: 8 }} />
          <Typography
            variant="h6"
            component="h3"
            style={{ fontWeight: 600, flex: 1 }}
          >
            {step.name}
          </Typography>
        </Box>

        <Box
          display={'flex'}
          alignItems={'center'}
          justifyContent={'flex-start'}
        >
          <Checkbox style={{ marginRight: '2px' }} />
          <Typography color="textSecondary" style={{ fontWeight: 600 }}>
            {step.description}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {step.seconds && (
            <Grid item>
              <Box display="flex" alignItems="center">
                <TimerIcon
                  fontSize="small"
                  style={{ marginRight: 4, opacity: 0.7 }}
                />
                <Typography variant="body2">
                  Duration: <strong>{step.seconds} seconds</strong>
                </Typography>
              </Box>
            </Grid>
          )}
          {/* {step.nextStep && (
            <Grid item>
              <Typography variant="body2">
                Next Step:{" "}
                <strong style={{ color: "#1976d2" }}>{step.nextStep}</strong>
              </Typography>
            </Grid>
          )} */}
        </Grid>
      </CardContent>
    </Card>
  );

  // Helper function to render form step details
  const renderFormStep = (step: FormStep, index: number) => (
    <Card
      elevation={2}
      style={{
        marginBottom: 24,
        borderLeft: "4px solid #f57c00",
        backgroundColor: "#fff8e1",
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" marginBottom={2}>
          <FormIcon color="secondary" style={{ marginRight: 8 }} />
          <Typography
            variant="h6"
            component="h3"
            style={{ fontWeight: 600, flex: 1 }}
          >
            {step.name}
          </Typography>
        </Box>

        {step.seconds && (
          <Box display="flex" alignItems="center" marginBottom={2}>
            <TimerIcon
              fontSize="small"
              style={{ marginRight: 4, opacity: 0.7 }}
            />
            <Typography variant="body2">
              Duration: <strong>{step.seconds} seconds</strong>
            </Typography>
          </Box>
        )}

        {step.conditions && step.conditions.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              style={{ fontWeight: 500, marginTop: 16 }}
            >
              Conditions ({step.conditions.length}):
            </Typography>
            <List dense>
              {step.conditions.map((condition, condIndex) => (
                <ListItem
                  key={condIndex}
                  style={{
                    paddingLeft: 0,
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <Box display="flex" alignItems="center" width="100%">
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 8, marginLeft: '10px' }}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 500 }}
                          >
                            {condition.name}
                          </Typography>
                          <Typography variant="body2" style={{ opacity: 0.2 }}>
                            _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _ _
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                  {/* {condition.values &&
                    condition.values.length > 0 &&
                    renderConditionValues(condition)} */}
                  {condIndex < step.conditions.length - 1 && (
                    <Divider style={{ marginTop: 16, marginBottom: 8 }} />
                  )}
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  // Loading state
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ marginTop: 16, color: "#666" }}>
          Loading Service Type Data...
        </Typography>
      </Box>
    );
  }

  // No data state
  if (!data || data.length === 0) {
    return (
      <Box textAlign="center" padding={8}>
        <InfoIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
        <Typography variant="h5" gutterBottom color="textSecondary">
          No Service Type Data Found
        </Typography>
        <Typography variant="body1" color="textSecondary">
          The requested service type could not be found or has no data.
        </Typography>
      </Box>
    );
  }

  const [serviceType] = data;
  const steps = serviceType?.steps || [];
  const taskSteps = steps.filter((step) => step.type === StepTypes.task);
  const formSteps = steps.filter((step) => step.type === StepTypes.form);
  const totalConditions = formSteps.reduce(
    (total, step) => total + (step?.conditions?.length || 0),
    0
  );

  return (
    <Box style={{ maxWidth: 1200, margin: "0 auto", padding: 24 }}>
      {/* Header Section */}
      {/* <Paper
        elevation={3}
        style={{
          padding: 32,
          marginBottom: 32,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          style={{ fontWeight: 700, textAlign: "center" }}
        >
          Service Type Overview
        </Typography>
        <Typography
          variant="h5"
          style={{ textAlign: "center", opacity: 0.9, fontWeight: 700 }}
        >
          {serviceType.name}
        </Typography>

        <Grid container spacing={3} style={{ marginTop: 16, justifyContent: 'space-between' }}>
          <Grid item md="auto">
            <Box display="flex" alignItems="center" marginBottom={1}>
              <DateIcon style={{ marginRight: 8, opacity: 0.8 }} />
              <Typography variant="body2" style={{ opacity: 0.8 }}>
                Created:
              </Typography>
            </Box>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {formatDate(serviceType.createdAt)}
            </Typography>
          </Grid>

          <Grid item md="auto">
            <Box display="flex" alignItems="center" marginBottom={1}>
              <SkillIcon style={{ marginRight: 8, opacity: 0.8 }} />
              <Typography variant="body2" style={{ opacity: 0.8 }}>
                Skills Required:
              </Typography>
            </Box>
            <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
              {serviceType.skills && serviceType.skills.length > 0 ? (
                serviceType.skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    size="small"
                    style={{
                      backgroundColor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: 500,
                    }}
                  />
                ))
              ) : (
                <Typography
                  variant="body2"
                  style={{ fontStyle: "italic", opacity: 0.7 }}
                >
                  No specific skills required
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper> */}

      {/* Steps Section */}
      {steps.length === 0 ? (
        <Paper
          elevation={2}
          style={{
            padding: 48,
            textAlign: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <TaskIcon style={{ fontSize: 64, color: "#ccc", marginBottom: 16 }} />
          <Typography variant="h6" gutterBottom color="textSecondary">
            No Process Steps Configured
          </Typography>
          <Typography variant="body1" color="textSecondary">
            This service type exists but has no defined process steps yet.
          </Typography>
        </Paper>
      ) : (
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            style={{ fontWeight: 600, marginBottom: 24, textAlign: 'center', textDecoration: 'underline' }}
          >
            {serviceType.name}
          </Typography>

          {steps.map((step, index) => (
            <Box key={index} style={{ marginBottom: 16 }}>
              <Typography
                variant="overline"
                style={{
                  display: "block",
                  fontWeight: 600,
                  color: "#1976d2",
                  fontSize: "0.875rem",
                  marginBottom: 8,
                }}
              >
                Step {index + 1} of {steps.length}
              </Typography>
              {step.type === StepTypes.task
                ? renderTaskStep(step as TaskStep, index)
                : renderFormStep(step as FormStep, index)}
            </Box>
          ))}
        </Box>
      )}

      {/* Fixed Print Button */}
      <Button
        className={classes.printBtn}
        variant="contained"
        color="primary"
        size="large"
        onClick={() => window.print()}
        style={{
          paddingLeft: 8,
          paddingRight: 8,
          paddingTop: 8,
          paddingBottom: 8,
          textTransform: "none",
        }}
      >
        <Print />
      </Button>
    </Box>
  );
}
